// Chemin: C:\gestion-copro\src\pages\vigie\CourrierDetailVigiePage.tsx
// Détail courrier VIGIE — Vue lecture + parcours guidé complet
// Session 100 : Placeholder
// Session 102 : Version fonctionnelle
// Session 113 : Chantier 2 — Parcours courrier guidé complet
// Session 126 : Ajout suppression courrier avec double sécurité (modale + saisie SUPPRIMER)
//               Finalisation conditionnelle : SCUD uniquement si brouillon modifié manuellement
//   - Modale SCUD avant finalisation (comme CS, adapté VIGIE)
//   - Sauvegarde analyse SCUD en Firestore
//   - Génération PDF (télécharger / ouvrir)
//   - Confirmation avant marquage envoyé
//   - Flux guidé : brouillon → analyse SCUD → finalisé → PDF → envoyé
// Session 127 : Fix appels PDF cassés (str_replace session 126)
//               Ajout bouton "Envoyer par email" (mailto:) — visible si finalisé + email destinataire
// Session 143 : Support courrier miroir (reçu via SENTINELLE) —
//               Statut 'recu' + bandeau cyan + section "Document original" avec lien GED
//               Label Expéditeur/Destinataire adapté selon le sens
// Session 179 : Bouton "Marquer résolu / Rouvrir" — toggle resolu sur courriers envoyés/reçus
//               Badge résolu dans l'en-tête + exclusion du contexte SENTINELLE

import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, deleteDoc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { useAuth } from '../../context/AuthContext';
import { useCopropriete } from '../../context/CoproprieteContext';
import {
  getCourrierById,
  soumettreCourrierValidation,
  marquerCommeEnvoye,
  analyserCourrierSCUD,
  sauvegarderAnalyseSCUD
} from '../../services/courrier.service';
import { getCopropriete } from '../../services/copropriete.service';
import { telechargerPDFCourrier, ouvrirPDFCourrier, genererPDFCourrier } from '../../services/pdfCourrier.service';
import { uploadDocument } from '../../services/document.service';
import { addEvenementToAffaire } from '../../services/affaire.service';
import { CategorieDocument } from '../../types/document.types';
import { Copropriete } from '../../types/copropriete.types';
import {
  Courrier,
  StatutCourrier,
  AnalyseSCUD,
  AlerteSCUD,
  TYPE_COURRIER_LABELS,
  MODE_ENVOI_LABELS,
  NIVEAU_RISQUE_LABELS,
  NIVEAU_RISQUE_COLORS,
  NIVEAU_CLASSIFICATION_LABELS,
  NIVEAU_CLASSIFICATION_COLORS,
  NIVEAU_CLASSIFICATION_DOT_COLORS,
  NIVEAU_CLASSIFICATION_DESCRIPTIONS
} from '../../types/courrier.types';
import {
  ArrowLeft,
  Edit2,
  Send,
  CheckCircle,
  FileText,
  User,
  Calendar,
  Loader2,
  AlertCircle,
  AlertTriangle,
  Shield,
  ShieldCheck,
  ShieldAlert,
  Lightbulb,
  Download,
  ExternalLink,
  X,
  Sparkles,
  Trash2,
  Mail,
  CheckCheck
} from 'lucide-react';

// Session 145 : Surcharge des labels classification pour le mode VIGIE
// En VIGIE, pas de vote CS — on remplace par des avertissements adaptés
// Session 301 : Labels et descriptions Orange dynamiques selon scoreRisque
//   Orange + scoreRisque faible → "Communication engageante — aucune correction nécessaire"
//   Orange + scoreRisque moyen  → "Vérification recommandée" (comportement d'origine)
const getVigieClassificationLabel = (niveau: string, scoreRisque?: string): string => {
  if (niveau === 'orange' && scoreRisque === 'faible') return 'Orange — Communication engageante';
  const labels: Record<string, string> = {
    vert: 'Vert — Envoi libre',
    orange: 'Orange — Vérification recommandée',
    rouge: 'Rouge — Attention requise',
  };
  return labels[niveau] || niveau;
};
const getVigieClassificationDescription = (niveau: string, scoreRisque?: string): string => {
  if (niveau === 'orange' && scoreRisque === 'faible') return 'Courrier engageant par nature (ex. mise en demeure), mais aucune correction de ton nécessaire.';
  const descriptions: Record<string, string> = {
    vert: 'Communication courante, aucun risque détecté.',
    orange: 'Formulation engageante détectée. Relisez attentivement les suggestions SENTINELLE.',
    rouge: 'Courrier à risque élevé. Relisez attentivement et appliquez les reformulations suggérées avant envoi.',
  };
  return descriptions[niveau] || '';
};

// ============================================
// UTILITAIRES
// ============================================

const formatDate = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const formatDateHeure = (timestamp: any): string => {
  if (!timestamp) return '';
  const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
  if (isNaN(date.getTime())) return '';
  return date.toLocaleDateString('fr-FR', {
    day: 'numeric', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit'
  });
};

const getStatutColor = (statut: string): string => {
  const colors: Record<string, string> = {
    brouillon: 'bg-gray-100 text-gray-700',
    en_validation: 'bg-yellow-100 text-yellow-700',
    valide: 'bg-blue-100 text-blue-700',
    envoye: 'bg-green-100 text-green-700',
    recu: 'bg-cyan-100 text-cyan-700',
    annule: 'bg-red-100 text-red-700',
    rejete: 'bg-orange-100 text-orange-700',
  };
  return colors[statut] || 'bg-gray-100 text-gray-700';
};

const getStatutLabel = (statut: string): string => {
  const labels: Record<string, string> = {
    brouillon: 'Brouillon',
    en_validation: 'En validation',
    valide: 'Finalisé — Prêt à envoyer',
    envoye: 'Envoyé',
    recu: 'Reçu',
    annule: 'Annulé',
    rejete: 'Rejeté',
  };
  return labels[statut] || statut;
};

/** Icône pour le type d'alerte SCUD */
const getAlerteIcon = (type: AlerteSCUD['type']) => {
  switch (type) {
    case 'terme_risque':
      return <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />;
    case 'accusation':
      return <AlertCircle className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />;
    case 'imperatif':
      return <AlertTriangle className="w-4 h-4 text-orange-500 flex-shrink-0 mt-0.5" />;
    case 'menace':
      return <ShieldAlert className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />;
    case 'emotion':
      return <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />;
    case 'juridique':
      return <Shield className="w-4 h-4 text-red-500 flex-shrink-0 mt-0.5" />;
    default:
      return <AlertTriangle className="w-4 h-4 text-gray-500 flex-shrink-0 mt-0.5" />;
  }
};

// ============================================
// COMPOSANT PRINCIPAL
// ============================================

const CourrierDetailVigiePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { userProfile, firebaseUser } = useAuth();
  const { coproprieteId } = useCopropriete();

  // Données
  const [courrier, setCourrier] = useState<Courrier | null>(null);
  const [copropriete, setCoproprieteData] = useState<Copropriete | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Actions
  const [actionEnCours, setActionEnCours] = useState<string | null>(null);

  // Modale SCUD
  const [showModaleSCUD, setShowModaleSCUD] = useState(false);
  const [analyseEnCours, setAnalyseEnCours] = useState(false);
  const [analyseResultat, setAnalyseResultat] = useState<AnalyseSCUD | null>(null);

  // Modale confirmation envoi
  const [showModaleEnvoi, setShowModaleEnvoi] = useState(false);

  // Session 126 : Suppression avec double confirmation
  const [showModaleSuppression, setShowModaleSuppression] = useState(false);
  const [confirmationTexte, setConfirmationTexte] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Nom utilisateur
  const getUserDisplayName = (): string => {
    if (userProfile?.prenom && userProfile?.nom) {
      return `${userProfile.prenom} ${userProfile.nom}`.trim();
    }
    return userProfile?.email || 'Utilisateur';
  };

  // ============================================
  // CHARGEMENT
  // ============================================

  useEffect(() => {
    const charger = async () => {
      if (!id || !coproprieteId) return;
      setLoading(true);
      try {
        const [courrierData, coproData] = await Promise.all([
          getCourrierById(id),
          getCopropriete(coproprieteId)
        ]);
        if (!courrierData) {
          setError('Courrier non trouvé');
          return;
        }
        setCourrier(courrierData);
        setCoproprieteData(coproData);

        // Si analyse SCUD déjà sauvegardée, la récupérer
        if (courrierData.analyseSentinelle) {
          setAnalyseResultat(courrierData.analyseSentinelle);
        }
      } catch (err) {
        console.error('Erreur:', err);
        setError('Impossible de charger le courrier');
      } finally {
        setLoading(false);
      }
    };
    charger();
  }, [id, coproprieteId]);

  // Session 287 : Déclenchement automatique SCUD si retour depuis modification manuelle
  // Correction session 286 : useRef initialisé au montage ne se réinitialise pas si le composant
  // est réutilisé par React Router (même route, même ID). La dépendance sur location.state?.lancerSCUD
  // garantit le ré-déclenchement même si le composant était déjà monté.
  // scudTraitéRef empêche le double déclenchement si courrier est rechargé après exécution.
  const scudTraitéRef = useRef(false);
  useEffect(() => {
    if (scudTraitéRef.current) return;
    if (!location.state?.lancerSCUD) return;
    if (!courrier || courrier.statut !== 'brouillon') return;
    scudTraitéRef.current = true;
    window.history.replaceState({}, document.title);
    handleLancerFinalisation();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [courrier, location.state?.lancerSCUD]);

  // ============================================
  // ACTIONS
  // ============================================

  /**
   * Session 126 : Finalisation directe (brouillon non modifié par l'utilisateur)
   * Pas d'appel API SENTINELLE — le courrier a déjà été rédigé par IA.
   */
  const handleFinaliserDirect = async () => {
    if (!courrier || !id || !firebaseUser || !userProfile) return;

    setActionEnCours('finaliser');
    try {
      const displayName = getUserDisplayName();
      await soumettreCourrierValidation(id, firebaseUser.uid, displayName);

      const updated = await getCourrierById(id);
      setCourrier(updated);
    } catch (err) {
      console.error('Erreur finalisation directe:', err);
      setError('Impossible de finaliser le courrier');
    } finally {
      setActionEnCours(null);
    }
  };

  /**
   * Étape 1 : Lancer l'analyse SCUD (ouvre la modale)
   * C'est le point d'entrée pour la finalisation
   */
  const handleLancerFinalisation = async () => {
    if (!courrier || !coproprieteId) return;

    setShowModaleSCUD(true);
    setAnalyseEnCours(true);
    setAnalyseResultat(null);
    setError(null);

    try {
      const analyse = await analyserCourrierSCUD(
        coproprieteId,
        courrier.objet,
        courrier.contenu,
        courrier.destinataire?.nom || '',
        courrier.type,
        'vigie'
      );
      setAnalyseResultat(analyse);

      // Session 301 : Sauvegarder immédiatement l'analyse en Firestore
      // afin que CourrierFormVigiePage puisse injecter les reformulations
      // si l'utilisateur choisit "Modifier le courrier" plutôt que de finaliser
      if (id) {
        try { await sauvegarderAnalyseSCUD(id, analyse); } catch (_) {}
      }

      // Si risque faible et aucune alerte → finaliser automatiquement
      if (analyse.scoreRisque === 'faible' && analyse.alertes.length === 0) {
        await finaliserCourrier(analyse);
      }
    } catch (err: unknown) {
      console.error('Erreur analyse SCUD:', err);
      const message = err instanceof Error ? err.message : '';
      if (message.includes('quota') || message.includes('Quota')) {
        setError('Quota SENTINELLE épuisé. Vous pouvez finaliser manuellement.');
      } else {
        setError('Erreur lors de l\'analyse SCUD');
      }
      setShowModaleSCUD(false);
    } finally {
      setAnalyseEnCours(false);
    }
  };

  /**
   * Étape 2 : Finaliser le courrier (sauvegarder analyse + passer en validé)
   */
  const finaliserCourrier = async (analyse: AnalyseSCUD) => {
    if (!courrier || !id || !firebaseUser || !userProfile) return;

    setActionEnCours('finaliser');
    try {
      // Sauvegarder l'analyse SCUD en Firestore
      await sauvegarderAnalyseSCUD(id, analyse);

      // Soumettre → passe directement en 'valide' pour VIGIE (votesRequis = 0)
      const displayName = getUserDisplayName();
      await soumettreCourrierValidation(id, firebaseUser.uid, displayName);

      // Session 272 : Propager statutCourrier 'finalise' sur le document GED miroir
      if (courrier.documentGedId) {
        try {
          await updateDoc(doc(db, 'documents', courrier.documentGedId), {
            statutCourrier: 'finalise',
          });
        } catch (gedErr) {
          console.warn('[CourrierDetail] Erreur propagation statut GED finalise:', gedErr);
        }
      }

      // Recharger le courrier mis à jour
      const updated = await getCourrierById(id);
      setCourrier(updated);
      setShowModaleSCUD(false);
    } catch (err) {
      console.error('Erreur finalisation:', err);
      setError('Impossible de finaliser le courrier');
    } finally {
      setActionEnCours(null);
    }
  };

  /** Valider malgré les alertes */
  const handleFinaliserQuandMeme = async () => {
    if (analyseResultat) {
      await finaliserCourrier(analyseResultat);
    }
  };

  /** Fermer la modale et aller modifier */
  const handleModifierDepuisModale = () => {
    setShowModaleSCUD(false);
    navigate(`/vigie/courriers/${id}/modifier`);
  };

  /**
   * Étape 3 : Génération PDF
   * Session 127 : fix appels cassés — rétablissement await telechargerPDFCourrier / ouvrirPDFCourrier
   */
  const handleTelechargerPDF = async () => {
    if (!courrier || !copropriete) return;

    setActionEnCours('pdf');
    try {
      const adresseCopropriete = (copropriete.adresse && typeof copropriete.adresse === 'object' && copropriete.adresse.rue)
        ? [copropriete.adresse.rue, [copropriete.adresse.codePostal, copropriete.adresse.ville].filter(Boolean).join(' ')].filter(Boolean).join('\n')
        : undefined;

      await telechargerPDFCourrier({
        courrier,
        nomCopropriete: copropriete.nom,
        adresseCopropriete,
        isVigie: true,
        nomSignataire: getUserDisplayName(),
      });
    } catch (err) {
      console.error('Erreur PDF:', err);
      setError('Erreur lors de la génération du PDF');
    } finally {
      setActionEnCours(null);
    }
  };

  const handleOuvrirPDF = async () => {
    if (!courrier || !copropriete) return;

    setActionEnCours('pdf');
    try {
      const adresseCopropriete = (copropriete.adresse && typeof copropriete.adresse === 'object' && copropriete.adresse.rue)
        ? [copropriete.adresse.rue, [copropriete.adresse.codePostal, copropriete.adresse.ville].filter(Boolean).join(' ')].filter(Boolean).join('\n')
        : undefined;

      await ouvrirPDFCourrier({
        courrier,
        nomCopropriete: copropriete.nom,
        adresseCopropriete,
        isVigie: true,
        nomSignataire: getUserDisplayName(),
      });
    } catch (err) {
      console.error('Erreur PDF:', err);
      setError('Erreur lors de l\'ouverture du PDF');
    } finally {
      setActionEnCours(null);
    }
  };

  /**
   * Session 127 : Envoyer par email via mailto:
   * Construit un lien mailto: avec destinataire, objet et corps pré-remplis.
   * Universel : fonctionne avec Gmail, Outlook, Yahoo, Thunderbird, Apple Mail...
   */
  const handleEnvoyerParEmail = () => {
    if (!courrier) return;

    const destinataireEmail = courrier.destinataire?.email || '';
    const objet = encodeURIComponent(courrier.objet || '');
    const corps = encodeURIComponent(
      `Madame, Monsieur,\n\nVeuillez trouver ci-joint le courrier ayant pour objet : "${courrier.objet}".\n\nCordialement,\n${getUserDisplayName()}`
    );

    const mailtoUrl = `mailto:${destinataireEmail}?subject=${objet}&body=${corps}`;
    const link = document.createElement('a');
    link.href = mailtoUrl;
    link.click();
  };

  /**
   * Étape 4 : Marquer comme envoyé (avec confirmation)
   */
  const handleConfirmerEnvoi = async () => {
    if (!courrier || !id || !firebaseUser || !userProfile || !copropriete || !coproprieteId) return;

    setActionEnCours('envoyer');
    try {
      const displayName = getUserDisplayName();
      await marquerCommeEnvoye(id, firebaseUser.uid, displayName, courrier.modeEnvoi || 'postal');

      // Session 272 : Propager statutCourrier 'envoye' + dateEnvoiCourrier sur le document GED miroir
      if (courrier.documentGedId) {
        try {
          await updateDoc(doc(db, 'documents', courrier.documentGedId), {
            statutCourrier: 'envoye',
            dateEnvoiCourrier: Timestamp.now(),
            sens: 'envoye',
          });
        } catch (gedErr) {
          console.warn('[CourrierDetail] Erreur propagation statut GED envoye:', gedErr);
        }
      }

      // Session 127 : Archivage automatique du PDF dans la GED
      try {
        // 1. Construire l'adresse copropriété (même logique que handleTelechargerPDF)
        const adrPostale = (copropriete as any).adressePostale;
        const adr = copropriete.adresse;
        let adresseCoproArchive: string | undefined;
        if (adrPostale && typeof adrPostale === 'object' && adrPostale.rue) {
          adresseCoproArchive = [adrPostale.rue, [adrPostale.codePostal, adrPostale.ville].filter(Boolean).join(' ')].filter(Boolean).join('\n');
        } else if (adr && typeof adr === 'object' && (adr as any).rue) {
          const a = adr as any;
          adresseCoproArchive = [a.rue, [a.codePostal, a.ville].filter(Boolean).join(' ')].filter(Boolean).join('\n');
        }

        // 2. Générer le PDF en blob
        const pdfBlob = await genererPDFCourrier({
          courrier,
          nomCopropriete: copropriete.nom,
          adresseCopropriete: adresseCoproArchive,
          isVigie: true,
          nomSignataire: displayName,
        });

        // 3. Convertir le blob en File pour uploadDocument
        const fileName = `${courrier.numero || 'COU'}_${new Date().toISOString().split('T')[0]}.pdf`;
        const pdfFile = new File([pdfBlob], fileName, { type: 'application/pdf' });

        // 4. Déduire la catégorie GED du destinataire
        const categorieCourrier: CategorieDocument = (() => {
          const destNom = (courrier.destinataire?.nom || '').toLowerCase();
          if (destNom.includes('syndic') || destNom.includes('square habitat')) return 'syndic';
          if (destNom.includes('kic') || destNom.includes('promoteur')) return 'promoteur';
          return 'administratif';
        })();

        // 5. Uploader dans la GED
        // Session 294 : Récupérer le documentId pour écrire statutCourrier + dateEnvoiCourrier
        const pdfDocumentId = await uploadDocument({
          coproprieteId,
          file: pdfFile,
          titre: `Courrier envoyé — ${courrier.objet}`,
          type: 'courrier',
          categorie: categorieCourrier,
          description: `Courrier ${courrier.numero || ''} envoyé le ${new Date().toLocaleDateString('fr-FR')} à ${courrier.destinataire?.nom || 'destinataire'}`,
          dateDocument: new Date(),
          visibilite: 'proprietaire',
          userId: firebaseUser.uid,
          userNom: displayName,
        });

        // Session 294 : Écrire statutCourrier + dateEnvoiCourrier sur le document PDF archivé
        if (pdfDocumentId) {
          await updateDoc(doc(db, 'documents', pdfDocumentId), {
            statutCourrier: 'envoye',
            dateEnvoiCourrier: Timestamp.now(),
            sens: 'envoye',
          });
        }

        console.log('[CourrierDetail VIGIE] PDF archivé dans la GED avec statut envoye');
      } catch (archiveErr) {
        console.error('[CourrierDetail VIGIE] Erreur archivage GED:', archiveErr);
        setError('Le courrier a été marqué envoyé, mais l\'archivage PDF dans vos documents a échoué. Vous pouvez archiver manuellement via "Télécharger PDF" puis "Mes documents".');
      }

      // Session 134 : Traçabilité chronologie — ajouter événement dans le dossier
      // Ajouté à l'ENVOI (pas au brouillon) pour éviter les traces fantômes
      if (courrier.affaireId) {
        try {
          await addEvenementToAffaire(courrier.affaireId, {
            type: 'courrier',
            titre: `Courrier envoyé: ${courrier.objet}`,
            auteurId: firebaseUser.uid,
            auteurNom: displayName,
            courrierId: id
          });
        } catch (errChrono) {
          // Non bloquant : le courrier est envoyé, seule la chronologie échoue
          console.error('[CourrierDetail VIGIE] Erreur ajout chronologie dossier:', errChrono);
        }
      }

      const updated = await getCourrierById(id);
      setCourrier(updated);
      setShowModaleEnvoi(false);
    } catch (err) {
      console.error('Erreur marquage envoyé:', err);
      setError('Impossible de marquer comme envoyé');
    } finally {
      setActionEnCours(null);
    }
  };

  /**
   * Session 126 : Suppression du courrier avec double sécurité
   */
  const handleSupprimerCourrier = async () => {
    if (!id) return;
    setDeleting(true);
    try {
      await deleteDoc(doc(db, 'courriers', id));
      navigate('/vigie/courriers');
    } catch (err) {
      console.error('Erreur suppression courrier:', err);
      setError('Erreur lors de la suppression du courrier');
      setDeleting(false);
      setShowModaleSuppression(false);
    }
  };

  /**
   * Session 179 : Toggle résolu/non résolu
   * Marque le courrier comme résolu (sujet traité) ou le rouvre.
   * Impact : les courriers resolu=true sont exclus du contexte SENTINELLE.
   */
  const handleToggleResolu = async () => {
    if (!id || !courrier) return;
    setActionEnCours('resolu');
    try {
      const newResolu = !courrier.resolu;
      await updateDoc(doc(db, 'courriers', id), { resolu: newResolu });
      setCourrier({ ...courrier, resolu: newResolu });
    } catch (err) {
      console.error('Erreur toggle résolu:', err);
      setError('Impossible de modifier le statut résolu');
    } finally {
      setActionEnCours(null);
    }
  };

  // ============================================
  // RENDU — ÉTATS SPÉCIAUX
  // ============================================

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-amber-500" />
      </div>
    );
  }

  if (error && !courrier) {
    return (
      <div className="max-w-3xl mx-auto space-y-4">
        <button onClick={() => navigate('/vigie/courriers')} className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700">
          <ArrowLeft className="w-4 h-4" /> Retour
        </button>
        <div className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5" />
          {error}
        </div>
      </div>
    );
  }

  if (!courrier) return null;

  // Session 127 : Logique d'affichage selon le mode d'envoi du courrier
  const isModeMail = courrier.modeEnvoi === 'email';
  const peutEnvoyerParEmail = isModeMail
    && (courrier.statut === 'valide' || courrier.statut === 'envoye')
    && courrier.destinataire?.email;

  // ============================================
  // RENDU PRINCIPAL
  // ============================================

  return (
    <div className="max-w-3xl mx-auto space-y-6">

      {/* Navigation */}
      <button
        onClick={() => navigate('/vigie/courriers')}
        className="inline-flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
      >
        <ArrowLeft className="w-4 h-4" />
        Retour aux courriers
      </button>

      {/* Erreur contextuelle */}
      {error && courrier && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg flex items-center gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          {error}
          <button onClick={() => setError(null)} className="ml-auto text-red-400 hover:text-red-600">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* ============================================ */}
      {/* EN-TÊTE + ACTIONS CONTEXTUELLES */}
      {/* ============================================ */}
      <div className="bg-white rounded-xl shadow-sm p-6">
        {/* Titre et métadonnées */}
        <div className="mb-4">
          <div className="flex items-center gap-3 mb-2 flex-wrap">
            {courrier.numero && (
              <span className="text-sm font-mono text-gray-400">{courrier.numero}</span>
            )}
            <span className={`px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatutColor(courrier.statut)}`}>
              {getStatutLabel(courrier.statut)}
            </span>
            <span className="px-2 py-0.5 bg-gray-100 rounded text-xs text-gray-600">
              {TYPE_COURRIER_LABELS[courrier.type] || courrier.type}
            </span>
            {/* Badge SCUD si analyse existante */}
            {analyseResultat?.scoreRisque && courrier.statut !== 'brouillon' && (
              <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${NIVEAU_RISQUE_COLORS[analyseResultat.scoreRisque]}`}>
                SCUD: {NIVEAU_RISQUE_LABELS[analyseResultat.scoreRisque]}
              </span>
            )}
            {/* Session 179 : Badge résolu */}
            {courrier.resolu && (
              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-100 text-emerald-700 flex items-center gap-1">
                <CheckCheck className="w-3 h-3" />
                Résolu
              </span>
            )}
          </div>
          <h1 className="text-xl font-bold text-gray-900">{courrier.objet}</h1>
          <p className="text-sm text-gray-500 mt-1">
            Créé le {formatDate(courrier.createdAt)}
            {courrier.dateEnvoi && ` · Envoyé le ${formatDate(courrier.dateEnvoi)}`}
          </p>
        </div>

        {/* Actions selon le statut — toujours en dessous */}
        <div className="flex items-center gap-2 flex-wrap">

            {/* BROUILLON : Modifier + Finaliser */}
            {courrier.statut === 'brouillon' && (
              <>
                <button
                  onClick={() => navigate(`/vigie/courriers/${id}/modifier`)}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Edit2 className="w-4 h-4" />
                  Modifier
                </button>
                {/* Session 126 : SCUD uniquement si modifié manuellement */}
                {courrier.modifieParUtilisateur ? (
                  <button
                    onClick={handleLancerFinalisation}
                    disabled={actionEnCours !== null}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50"
                  >
                    <Sparkles className="w-4 h-4" />
                    Vérifier et finaliser
                  </button>
                ) : (
                  <button
                    onClick={handleFinaliserDirect}
                    disabled={actionEnCours !== null}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                  >
                    <CheckCircle className="w-4 h-4" />
                    Finaliser
                  </button>
                )}
              </>
            )}

            {/* FINALISÉ : PDF + Email + Marquer envoyé */}
            {courrier.statut === 'valide' && (
              <>
                <button
                  onClick={handleTelechargerPDF}
                  disabled={actionEnCours === 'pdf'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {actionEnCours === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Télécharger PDF
                </button>
                <button
                  onClick={handleOuvrirPDF}
                  disabled={actionEnCours === 'pdf'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  <ExternalLink className="w-4 h-4" />
                  Aperçu
                </button>
                {/* Session 127 : Bouton email — ouvre Gmail compose avec le contenu du courrier */}
                {peutEnvoyerParEmail && (
                  <a
                    href={`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(courrier.destinataire?.email || '')}&su=${encodeURIComponent(courrier.objet || '')}&body=${encodeURIComponent(courrier.contenu || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Envoyer par email
                  </a>
                )}
                <button
                  onClick={() => setShowModaleEnvoi(true)}
                  disabled={actionEnCours !== null}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50"
                >
                  <Send className="w-4 h-4" />
                  Marquer envoyé
                </button>
              </>
            )}

            {/* ENVOYÉ : PDF + Email (re-envoi possible) */}
            {courrier.statut === 'envoye' && (
              <>
                <button
                  onClick={handleTelechargerPDF}
                  disabled={actionEnCours === 'pdf'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
                >
                  {actionEnCours === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                  Télécharger PDF
                </button>
                {/* Session 127 : Bouton email aussi disponible sur courrier envoyé (re-envoi) */}
                {peutEnvoyerParEmail && (
                  <a
                    href={`https://mail.google.com/mail/?view=cm&to=${encodeURIComponent(courrier.destinataire?.email || '')}&su=${encodeURIComponent(courrier.objet || '')}&body=${encodeURIComponent(courrier.contenu || '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-blue-300 rounded-lg text-blue-700 hover:bg-blue-50 transition-colors"
                  >
                    <Mail className="w-4 h-4" />
                    Envoyer par email
                  </a>
                )}
              </>
            )}

            {/* Session 179 : REÇU — PDF du document original */}
            {courrier.statut === 'recu' && (
              <button
                onClick={handleTelechargerPDF}
                disabled={actionEnCours === 'pdf'}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                {actionEnCours === 'pdf' ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Télécharger PDF
              </button>
            )}

            {/* Session 179 : Toggle résolu — visible sur courriers envoyés et reçus */}
            {(courrier.statut === 'envoye' || courrier.statut === 'recu') && (
              <button
                onClick={handleToggleResolu}
                disabled={actionEnCours === 'resolu'}
                className={`inline-flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg transition-colors disabled:opacity-50 ${
                  courrier.resolu
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'border border-emerald-300 text-emerald-700 hover:bg-emerald-50'
                }`}
                title={courrier.resolu ? 'Rouvrir ce sujet' : 'Marquer ce sujet comme résolu'}
              >
                {actionEnCours === 'resolu' ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <CheckCheck className="w-4 h-4" />
                )}
                {courrier.resolu ? 'Résolu' : 'Marquer résolu'}
              </button>
            )}

            {/* Session 126 : Supprimer — disponible sur tous les statuts */}
            <button
              onClick={() => setShowModaleSuppression(true)}
              className="inline-flex items-center gap-1.5 px-3 py-2 text-sm border border-red-200 rounded-lg text-red-600 hover:bg-red-50 transition-colors"
              title="Supprimer ce courrier"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
      </div>

      {/* ============================================ */}
      {/* BANDEAU SUCCÈS après finalisation */}
      {/* ============================================ */}
      {courrier.statut === 'valide' && (
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 flex items-start gap-3">
          <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
            <CheckCircle className="w-4 h-4 text-blue-600" />
          </div>
          <div>
            <p className="font-medium text-blue-900">
              Courrier finalisé — {isModeMail ? 'Envoi par email' : 'Envoi par courrier postal'}
            </p>
            <p className="text-sm text-blue-700 mt-0.5">
              {isModeMail
                ? 'Cliquez sur "Envoyer par email" pour ouvrir votre messagerie avec le contenu pré-rempli, puis cliquez sur "Marquer envoyé" pour tracer l\'envoi.'
                : 'Téléchargez le PDF pour l\'imprimer et l\'envoyer par courrier postal, puis cliquez sur "Marquer envoyé" pour tracer l\'envoi.'
              }
            </p>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* DESTINATAIRE */}
      {/* ============================================ */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <User className="w-4 h-4 text-gray-400" />
          {/* Session 143 : Label adapté selon le sens */}
          {(courrier as any).sens === 'entrant' ? 'Expéditeur' : 'Destinataire'}
        </h2>
        <div className="space-y-2 text-sm">
          <div className="flex gap-2">
            <span className="text-gray-500 w-24">Nom</span>
            <span className="font-medium text-gray-900">{courrier.destinataire?.nom || 'Non renseigné'}</span>
          </div>
          {courrier.destinataire?.adresse && (
            <div className="flex gap-2">
              <span className="text-gray-500 w-24">Adresse</span>
              <span className="text-gray-700 whitespace-pre-line">{courrier.destinataire.adresse}</span>
            </div>
          )}
          {courrier.destinataire?.email && (
            <div className="flex gap-2">
              <span className="text-gray-500 w-24">Email</span>
              <span className="text-gray-700">{courrier.destinataire.email}</span>
            </div>
          )}
          <div className="flex gap-2">
            <span className="text-gray-500 w-24">Mode d'envoi</span>
            <span className="text-gray-700">{MODE_ENVOI_LABELS[courrier.modeEnvoi] || courrier.modeEnvoi}</span>
          </div>
        </div>
      </div>

      {/* ============================================ */}
      {/* CONTENU DU COURRIER */}
      {/* ============================================ */}
      <div className="bg-white rounded-xl shadow-sm p-5">
        <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <FileText className="w-4 h-4 text-gray-400" />
          Contenu
        </h2>
        <div className="prose prose-sm max-w-none text-gray-700 whitespace-pre-wrap bg-gray-50 rounded-lg p-4 border">
          {courrier.contenu}
        </div>
      </div>

      {/* ============================================ */}
      {/* SESSION 143 : DOCUMENT ORIGINAL (courrier miroir) */}
      {/* Lien direct vers la fiche GED pour aperçu / téléchargement */}
      {/* ============================================ */}
      {(courrier as any).documentGedId && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="font-semibold text-gray-900 mb-3 flex items-center gap-2">
            <FileText className="w-4 h-4 text-gray-400" />
            Document original
          </h2>
          <p className="text-sm text-gray-500 mb-3">
            Cette correspondance a été archivée via SENTINELLE. Le document original est disponible dans vos documents.
          </p>
          <button
            onClick={() => navigate(`/vigie/documents/${(courrier as any).documentGedId}`)}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-amber-50 border border-amber-200 text-amber-700 rounded-lg hover:bg-amber-100 transition-colors font-medium text-sm"
          >
            <ExternalLink className="w-4 h-4" />
            Voir le document dans mes documents
          </button>
        </div>
      )}

      {/* ============================================ */}
      {/* ANALYSE SCUD (affichée si disponible, hors brouillon) */}
      {/* ============================================ */}
      {analyseResultat && courrier.statut !== 'brouillon' && (
        <div className="bg-white rounded-xl shadow-sm p-5">
          <h2 className="font-semibold text-gray-900 mb-4 flex items-center gap-2">
            <Shield className="w-4 h-4 text-gray-400" />
            Analyse SENTINELLE (SCUD)
          </h2>

          {/* Niveau de classification */}
          {analyseResultat.niveauClassification && (
            <div className="text-center mb-4">
              <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-sm font-bold border ${NIVEAU_CLASSIFICATION_COLORS[analyseResultat.niveauClassification]}`}>
                <span className={`w-3 h-3 rounded-full ${NIVEAU_CLASSIFICATION_DOT_COLORS[analyseResultat.niveauClassification]}`} />
                {getVigieClassificationLabel(analyseResultat.niveauClassification, analyseResultat.scoreRisque)}
              </div>
            </div>
          )}

          {/* Score risque */}
          <div className="text-center mb-4">
            <span className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium ${NIVEAU_RISQUE_COLORS[analyseResultat.scoreRisque]}`}>
              {analyseResultat.scoreRisque === 'faible' && <ShieldCheck className="w-4 h-4" />}
              {analyseResultat.scoreRisque === 'moyen' && <Shield className="w-4 h-4" />}
              {analyseResultat.scoreRisque === 'eleve' && <ShieldAlert className="w-4 h-4" />}
              {NIVEAU_RISQUE_LABELS[analyseResultat.scoreRisque]}
            </span>
          </div>

          {/* Analyse complète */}
          {analyseResultat.analyseComplete && (
            <p className="text-sm text-gray-600 text-center mb-4">{analyseResultat.analyseComplete}</p>
          )}

          {/* Alertes (résumé compact) */}
          {analyseResultat.alertes && analyseResultat.alertes.length > 0 && (
            <div className="border-t pt-3 mt-3">
              <p className="text-xs font-medium text-gray-500 uppercase mb-2">
                Points d'attention ({analyseResultat.alertes.length})
              </p>
              <div className="space-y-1.5">
                {analyseResultat.alertes.map((alerte: AlerteSCUD, i: number) => (
                  <div key={i} className="flex items-start gap-2 text-sm text-gray-600">
                    {getAlerteIcon(alerte.type)}
                    <span>{alerte.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ============================================ */}
      {/* HISTORIQUE (envoi tracé) */}
      {/* ============================================ */}
      {courrier.statut === 'envoye' && courrier.dateEnvoi && (
        <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
          <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Send className="w-4 h-4 text-green-600" />
          </div>
          <div>
            <p className="font-medium text-green-900">Courrier envoyé</p>
            <p className="text-sm text-green-700 mt-0.5">
              Marqué comme envoyé le {formatDateHeure(courrier.dateEnvoi)}
              {courrier.modeEnvoi && ` · ${MODE_ENVOI_LABELS[courrier.modeEnvoi] || courrier.modeEnvoi}`}
            </p>
          </div>
        </div>
      )}

      {/* Session 143 : Bandeau correspondance reçue */}
      {courrier.statut === 'recu' && (
        <div className="bg-cyan-50 border border-cyan-200 rounded-xl p-4 flex items-start gap-3">
          <div className="w-8 h-8 bg-cyan-100 rounded-full flex items-center justify-center flex-shrink-0">
            <Mail className="w-4 h-4 text-cyan-600" />
          </div>
          <div>
            <p className="font-medium text-cyan-900">Correspondance reçue</p>
            <p className="text-sm text-cyan-700 mt-0.5">
              Archivée via SENTINELLE le {formatDateHeure(courrier.createdAt)}
              {courrier.modeEnvoi && ` · ${MODE_ENVOI_LABELS[courrier.modeEnvoi] || courrier.modeEnvoi}`}
            </p>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODALE : ANALYSE SCUD (avant finalisation) */}
      {/* ============================================ */}
      {showModaleSCUD && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">

            {/* Header modale */}
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-blue-600" />
                <h2 className="text-lg font-bold text-gray-900">Analyse SENTINELLE</h2>
              </div>
              {!analyseEnCours && (
                <button onClick={() => setShowModaleSCUD(false)} className="text-gray-400 hover:text-gray-600">
                  <X className="w-5 h-5" />
                </button>
              )}
            </div>

            {/* Corps modale */}
            <div className="p-6">
              {analyseEnCours ? (
                /* Écran de chargement */
                <div className="flex flex-col items-center justify-center py-12">
                  <div className="relative mb-6">
                    <div className="w-20 h-20 rounded-full bg-blue-50 flex items-center justify-center">
                      <Loader2 className="w-10 h-10 text-blue-500 animate-spin" />
                    </div>
                    <div className="absolute -bottom-1 -right-1 w-8 h-8 rounded-full bg-amber-400 flex items-center justify-center">
                      <Shield className="w-4 h-4 text-white" />
                    </div>
                  </div>
                  <p className="text-lg font-medium text-gray-700">SENTINELLE vérifie votre courrier...</p>
                  <p className="text-sm text-gray-400 mt-2">Analyse SCUD : ton diplomatique, risque juridique, formulation</p>
                </div>
              ) : analyseResultat ? (
                /* Résultat de l'analyse */
                <div className="space-y-5">

                  {/* Niveau de classification */}
                  {analyseResultat.niveauClassification && (
                    <div className="text-center">
                      <div className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-lg font-bold border-2 ${NIVEAU_CLASSIFICATION_COLORS[analyseResultat.niveauClassification]}`}>
                        <span className={`w-3.5 h-3.5 rounded-full ${NIVEAU_CLASSIFICATION_DOT_COLORS[analyseResultat.niveauClassification]}`} />
                        {getVigieClassificationLabel(analyseResultat.niveauClassification, analyseResultat.scoreRisque)}
                      </div>
                      <p className="mt-2 text-sm text-gray-500">
                        {getVigieClassificationDescription(analyseResultat.niveauClassification, analyseResultat.scoreRisque)}
                      </p>
                    </div>
                  )}

                  {/* Score de risque */}
                  <div className="text-center">
                    <div className={`inline-flex items-center gap-2 px-4 py-2 rounded-full text-lg font-semibold ${NIVEAU_RISQUE_COLORS[analyseResultat.scoreRisque]}`}>
                      {analyseResultat.scoreRisque === 'faible' && <ShieldCheck className="w-6 h-6" />}
                      {analyseResultat.scoreRisque === 'moyen' && <Shield className="w-6 h-6" />}
                      {analyseResultat.scoreRisque === 'eleve' && <ShieldAlert className="w-6 h-6" />}
                      {NIVEAU_RISQUE_LABELS[analyseResultat.scoreRisque]}
                    </div>
                    {analyseResultat.analyseComplete && (
                      <p className="mt-3 text-gray-700 text-sm">{analyseResultat.analyseComplete}</p>
                    )}
                  </div>

                  {/* Alertes */}
                  {analyseResultat.alertes.length > 0 && (
                    <div>
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase mb-3">
                        <AlertTriangle className="w-4 h-4" />
                        Points d'attention ({analyseResultat.alertes.length})
                      </h3>
                      <div className="space-y-3">
                        {analyseResultat.alertes.map((alerte, index) => (
                          <div
                            key={index}
                            className={`p-3 rounded-lg border ${
                              alerte.type === 'accusation' || alerte.type === 'menace' || alerte.type === 'juridique'
                                ? 'bg-red-50 border-red-200'
                                : 'bg-amber-50 border-amber-200'
                            }`}
                          >
                            <div className="flex items-start gap-2">
                              {getAlerteIcon(alerte.type)}
                              <div className="flex-1">
                                <p className={`text-sm font-medium ${
                                  alerte.type === 'accusation' || alerte.type === 'menace' || alerte.type === 'juridique'
                                    ? 'text-red-800' : 'text-amber-800'
                                }`}>
                                  {alerte.message}
                                </p>
                                {alerte.extrait && (
                                  <p className="mt-1 text-xs italic text-gray-500">"{alerte.extrait}"</p>
                                )}
                                {alerte.suggestion && (
                                  <div className="mt-1.5 p-2 bg-green-50 rounded border border-green-200">
                                    <p className="text-xs text-green-800">
                                      <Lightbulb className="w-3.5 h-3.5 inline mr-1" />
                                      {alerte.suggestion}
                                    </p>
                                  </div>
                                )}
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Recommandations */}
                  {analyseResultat.recommandations && analyseResultat.recommandations.length > 0 && (
                    <div>
                      <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-700 uppercase mb-2">
                        <Lightbulb className="w-4 h-4" />
                        Recommandations
                      </h3>
                      <ul className="space-y-1.5">
                        {analyseResultat.recommandations.map((rec, index) => (
                          <li key={index} className="flex items-start gap-2 text-sm text-gray-700">
                            <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0 mt-0.5" />
                            {rec}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : null}
            </div>

            {/* Actions modale */}
            {!analyseEnCours && analyseResultat && (
              <div className="flex flex-col gap-3 p-4 border-t border-gray-200 bg-gray-50 rounded-b-xl">
                {analyseResultat.scoreRisque !== 'faible' || analyseResultat.alertes.length > 0 ? (
                  /* Alertes présentes → choix modifier ou valider quand même */
                  <>
                    <p className="text-xs text-center text-gray-500">
                      SENTINELLE recommande de vérifier les points ci-dessus avant envoi.
                    </p>
                    <div className="flex justify-end gap-3">
                      <button
                        onClick={handleModifierDepuisModale}
                        className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors flex items-center gap-2"
                      >
                        <Edit2 className="w-4 h-4" />
                        Modifier le courrier
                      </button>
                      <button
                        onClick={handleFinaliserQuandMeme}
                        disabled={actionEnCours === 'finaliser'}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg font-medium hover:bg-amber-700 transition-colors disabled:opacity-50 flex items-center gap-2"
                      >
                        {actionEnCours === 'finaliser' ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <CheckCircle className="w-4 h-4" />
                        )}
                        Finaliser quand même
                      </button>
                    </div>
                  </>
                ) : (
                  /* Risque faible, pas d'alerte → auto-finalisé, bouton de confirmation */
                  <div className="text-center">
                    <button
                      onClick={() => setShowModaleSCUD(false)}
                      className="px-5 py-2.5 bg-green-600 text-white rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center gap-2 mx-auto"
                    >
                      <CheckCircle className="w-4 h-4" />
                      Parfait, courrier finalisé !
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODALE : CONFIRMATION ENVOI */}
      {/* ============================================ */}
      {showModaleEnvoi && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isModeMail ? 'bg-blue-100' : 'bg-green-100'}`}>
                  {isModeMail
                    ? <Mail className="w-5 h-5 text-blue-600" />
                    : <Send className="w-5 h-5 text-green-600" />
                  }
                </div>
                <h2 className="text-lg font-bold text-gray-900">
                  {isModeMail ? 'Confirmer l\'envoi par email' : 'Confirmer l\'envoi postal'}
                </h2>
              </div>

              <p className="text-gray-600 mb-2">
                {isModeMail
                  ? `Vous confirmez avoir envoyé ce courrier par email à ${courrier.destinataire?.email || 'destinataire'} ?`
                  : 'Vous confirmez avoir imprimé et envoyé ce courrier par voie postale ?'
                }
              </p>
              <p className="text-sm text-gray-500 mb-6">
                Cette action est <strong>définitive</strong> et sera tracée avec la date et l'heure.
                {isModeMail
                  ? ' Le courrier sera archivé automatiquement dans vos documents.'
                  : ' Assurez-vous d\'avoir téléchargé le PDF et posté le courrier avant de confirmer.'
                }
              </p>

              <div className="flex justify-end gap-3">
                <button
                  onClick={() => setShowModaleEnvoi(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  onClick={handleConfirmerEnvoi}
                  disabled={actionEnCours === 'envoyer'}
                  className={`px-4 py-2 text-white rounded-lg font-medium transition-colors disabled:opacity-50 flex items-center gap-2 ${isModeMail ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'}`}
                >
                  {actionEnCours === 'envoyer' ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : isModeMail ? (
                    <Mail className="w-4 h-4" />
                  ) : (
                    <Send className="w-4 h-4" />
                  )}
                  {isModeMail ? 'Confirmer l\'envoi email' : 'Confirmer l\'envoi postal'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============================================ */}
      {/* MODALE : SUPPRESSION AVEC DOUBLE SÉCURITÉ    */}
      {/* ============================================ */}
      {showModaleSuppression && courrier && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full">
            <div className="p-6">
              <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-red-100">
                <Trash2 className="w-6 h-6 text-red-600" />
              </div>
              <h2 className="text-lg font-bold text-gray-900 text-center mb-2">
                Supprimer ce courrier ?
              </h2>
              <p className="text-sm font-medium text-gray-900 text-center mb-2 px-4 py-2 bg-gray-100 rounded">
                {courrier.objet}
              </p>
              <p className="text-sm text-gray-500 text-center mb-4">
                Cette action est irréversible. Le courrier, son analyse SCUD et son historique seront définitivement supprimés.
              </p>

              {/* Double sécurité : taper SUPPRIMER */}
              <div className="mb-4">
                <label className="block text-sm text-gray-600 mb-1">
                  Tapez <span className="font-bold text-red-600">SUPPRIMER</span> pour confirmer
                </label>
                <input
                  type="text"
                  value={confirmationTexte}
                  onChange={(e) => setConfirmationTexte(e.target.value)}
                  placeholder="SUPPRIMER"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500 text-center font-mono"
                  autoFocus
                />
              </div>

              <div className="flex gap-3">
                <button
                  onClick={() => { setShowModaleSuppression(false); setConfirmationTexte(''); }}
                  disabled={deleting}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                >
                  Annuler
                </button>
                <button
                  onClick={handleSupprimerCourrier}
                  disabled={deleting || confirmationTexte !== 'SUPPRIMER'}
                  className="flex-1 px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                >
                  {deleting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
                  Supprimer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

    </div>
  );
};

export default CourrierDetailVigiePage;
